import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  EntityState,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit';
import { DashboardSmartView, DashboardSmartViewBase } from '../gzDashboardSmartViewsList/types';
import { RootState } from '../../redux/store';
import { RenameViewActionInterface, ToggleIsOpenStateForCategoryInterface } from '../types/redux.actions';
import { DashboardsReduxActions } from '../types/types';
import {
  checkIfWidgetsChanged,
  selectActiveWidgets,
  selectInactiveWidgets,
  selectWidgets,
  widgetsAdapter,
} from './dashboards.widgets.slice';
import {
  checkIfFiltersChanged,
  filtersAdapter,
  selectActiveFilters,
  selectDashboardFilterConfigs,
  selectDashboardFilterValues,
} from './dashboard.filters.slice';
import { DashboardAppState, selectDashboardsAppState } from './dashboards.app.reducer';
import { FilterConfigFrontend, UnifiedFilterConfig } from '../gzDashboardFilters/types';
import { AllFilterTypeConditions } from '@nirvana/shared/dtos/types/shared/filters';
import { cloneDeep } from 'lodash';
import { GzToast, ToastSeverity } from '../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../ui/gzToastNotification';
import {
  ActiveViewProps,
  CategoryGroup,
  ChangeActiveViewRequest,
  ChangeActiveViewResponse,
  CreateViewRequest,
  CreateViewResponse,
  DashboardType,
  DeleteViewRequest,
  DeleteViewResponse,
  Filter,
  FilterKeys,
  SetSmartViewCategoryStatusRequest,
  SetSmartViewCategoryStatusResponse,
  SetSmartViewsPanelStatusRequest,
  SetSmartViewsPanelStatusResponse,
  SmartViewCategoryConfig,
  UpdateDashboardConfigForUserRequest,
  UpdateDashboardConfigForUserResponse,
  UpdateViewRequest,
  UpdateViewResponse,
  ViewType,
} from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { DashboardReduxWidget } from './types';
import { MicroserviceRequest } from '../../utils/request/microservice.request';
import {
  dashboardConfigurationSelector,
  selectShouldDisplayDiscardPopUp,
  selectShouldDisplayUnsavedChangesPopUp,
  toggleLiveViewActiveStateActionHandler,
} from './dashboards.ui.slice';
import { GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME, GenericDashboardApiMethods } from '../types/constants';
import { EntitiesHelper } from '../utils/entities.helper';
import { translateFn } from '../../utils/translator.helper';
import { FiltersHelper } from '../gzDashboardFilters/helpers/filters.helper';

export const smartViewsCategoriesAdapter = createEntityAdapter<SmartViewCategoryConfig>({
  selectId: (category: SmartViewCategoryConfig) => category.id ?? '',
});

export const smartViewsAdapter = createEntityAdapter<DashboardSmartView>({
  selectId: (smartView: DashboardSmartView) => smartView.id ?? '',
});

export interface DashboardSmartViewsSliceState {
  views: EntityState<DashboardSmartView>;
  categories: EntityState<SmartViewCategoryConfig>;
  hasUnsavedChanges: boolean;
  activeSmartViewId: string;
  searchText: string;
  gridStackResetNeeded: boolean;
  gridStackFirstInitOfWidgets: boolean;
}

export const smartViewsInitialState: DashboardSmartViewsSliceState = {
  views: smartViewsAdapter.getInitialState({}),
  categories: smartViewsCategoriesAdapter.getInitialState({}),
  hasUnsavedChanges: false,
  activeSmartViewId: '',
  searchText: '',
  gridStackResetNeeded: false,
  gridStackFirstInitOfWidgets: true,
};

// SELECTORS
export const smartViewsSelectors = smartViewsAdapter.getSelectors((state: DashboardAppState) => state.viewsSlice.views);

export const smartViewsCategoriesSelectors = smartViewsCategoriesAdapter.getSelectors(
  (state: DashboardAppState) => state.viewsSlice.categories
);

export const selectAllSmartViewsCategories = (state: DashboardAppState): Array<SmartViewCategoryConfig> =>
  smartViewsCategoriesSelectors.selectAll(state);

export const selectAllSmartViews = (state: DashboardAppState): Array<DashboardSmartView> =>
  smartViewsSelectors.selectAll(state);

export const selectSmartViewById = (state: DashboardAppState, smartViewId: string): DashboardSmartView => {
  const smartView = smartViewsSelectors.selectById(state, smartViewId);
  if (!smartView) {
    throw new Error(`Could not find smart view with id ${smartViewId}`);
  }
  return smartView;
};

export const selectActiveWidgetsIdsFromActiveSmartView = (state: DashboardAppState): Array<string> => {
  return selectActiveViewConfiguration(state).widgets.map(widget => widget.id);
};

export const selectSearchTextFromSmartViews = (state: DashboardAppState): string => state.viewsSlice.searchText;

export const selectActiveViewId = (state: DashboardAppState): string => state.viewsSlice.activeSmartViewId;

export const selectActiveViewProps = (state: DashboardAppState): ActiveViewProps => {
  return {
    id: state.viewsSlice.activeSmartViewId,
    type: selectSmartViewById(state, state.viewsSlice.activeSmartViewId).type,
  };
};

export const selectDefaultView = (state: DashboardAppState): DashboardSmartView => {
  const smartViews = selectAllSmartViews(state);
  // Array.find returns the first matched element
  const defaultView = smartViews.find(view => view.type === ViewType.DEFAULT);
  if (!defaultView) {
    throw new Error('Invalid config object, no view assigned as default!');
  }
  return defaultView;
};

export const selectGridStackReset = (state: DashboardAppState): boolean => state.viewsSlice.gridStackResetNeeded;

export const selectGridStackFirstInitOfWidgets = (state: DashboardAppState): boolean =>
  state.viewsSlice.gridStackFirstInitOfWidgets;

export const selectDashboardHasUnsavedChanges = (state: DashboardAppState): boolean =>
  state.viewsSlice.hasUnsavedChanges;

export const checkIfHasUnsavedChanges = (state: DashboardAppState): boolean => {
  return checkIfFiltersChanged(state) || checkIfWidgetsChanged(state);
};

export const selectFilteredViewsForDashboard = (state: DashboardAppState): Array<DashboardSmartView> => {
  const searchText = selectSearchTextFromSmartViews(state);
  return selectAllSmartViews(state).filter((view: DashboardSmartView) =>
    view.label.trim().toLowerCase().includes(searchText)
  );
};

export const selectActiveViewConfiguration = (state: DashboardAppState): DashboardSmartView => {
  const activeViewId = selectActiveViewId(state);
  const activeViewConfiguration = smartViewsSelectors.selectById(state, activeViewId);
  if (!activeViewConfiguration) {
    throw new Error(`Could not find active view configuration with id ${activeViewId}. Are smart views enabled?`);
  }
  return activeViewConfiguration;
};

export const toggleIsOpenStateForSmartViewsCategory = createAsyncThunk<
  SmartViewCategoryConfig,
  ToggleIsOpenStateForCategoryInterface
>(
  DashboardsReduxActions.TOGGLE_IS_OPEN_STATE_FOR_SMART_VIEWS_CATEGORY,
  async (payload: ToggleIsOpenStateForCategoryInterface, { getState }): Promise<SmartViewCategoryConfig> => {
    const rootState: RootState = getState() as RootState;
    const dashboardAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardAppState).dashboardType;

    const request: SetSmartViewCategoryStatusRequest = {
      dashboardType: dashboardType,
      categoryId: payload.category.id,
      isSmartViewCategoryOpen: payload.isOpen,
    };

    await MicroserviceRequest.makeRequest<SetSmartViewCategoryStatusRequest, SetSmartViewCategoryStatusResponse>(
      GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME,
      GenericDashboardApiMethods.SET_SMART_VIEW_CATEGORY_STATUS,
      request
    );

    return {
      ...payload.category,
      isOpen: payload.isOpen,
    };
  }
);

export const toggleCollapsedStateForSmartViewsCategoryFulfilledActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<SmartViewCategoryConfig>
): void => {
  smartViewsCategoriesAdapter.upsertOne(state.viewsSlice.categories, action.payload);
};

export const setResetGridStack = createAction<boolean>(DashboardsReduxActions.SET_RESET_GRID_STACK);

export const setResetGridStackActionHandler = (state: DashboardAppState, action: PayloadAction<boolean>): void => {
  state.viewsSlice.gridStackResetNeeded = action.payload;
};

export const expandActiveSmartViewCategory = createAction<string>(
  DashboardsReduxActions.EXPAND_ACTIVE_SMART_VIEW_CATEGORY
);

export const expandActiveSmartViewCategoryActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<string>
): void => {
  smartViewsCategoriesAdapter.updateOne(state.viewsSlice.categories, {
    changes: { isOpen: true },
    id: action.payload,
  });
  // TODO call API to persist
};

// SMART VIEWS ACTIONS
export const setSmartViews = createAction<Array<DashboardSmartView>>(DashboardsReduxActions.SET_SMART_VIEWS);
export const setSmartViewsActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<Array<DashboardSmartView>>
): void => {
  smartViewsAdapter.setAll(state.viewsSlice.views, action.payload);
};

export const setSmartViewsCategories = createAction<Array<SmartViewCategoryConfig>>(
  DashboardsReduxActions.SET_SMART_VIEWS_CATEGORIES
);
export const setSmartViewsCategoriesActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<Array<SmartViewCategoryConfig>>
): void => {
  smartViewsCategoriesAdapter.setAll(state.viewsSlice.categories, action.payload);
};

export const changeActiveSmartView = createAsyncThunk<DashboardSmartView, string>(
  DashboardsReduxActions.CHANGE_ACTIVE_SMART_VIEW,
  async (payload: string, { getState }): Promise<DashboardSmartView> => {
    const rootState: RootState = getState() as RootState;
    const dashboardAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardAppState).dashboardType;

    const selectedSmartViewProps: DashboardSmartView = selectSmartViewById(dashboardAppState, payload);

    const request: ChangeActiveViewRequest = {
      dashboardType: dashboardType,
      viewProps: {
        id: payload,
        type: selectedSmartViewProps.type,
      },
    };

    const response: ChangeActiveViewResponse = await MicroserviceRequest.makeRequest<
      ChangeActiveViewRequest,
      ChangeActiveViewResponse
    >(GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME, GenericDashboardApiMethods.CHANGE_ACTIVE_VIEW, request);

    // We need to use the view from the response in order to update potentially invalid filters
    return EntitiesHelper.getSmartViewsForRedux(
      [response.viewConfig],
      response.viewConfig.id
    ).pop() as DashboardSmartView;
  }
);

export const setReduxStateFromSmartViewConfiguration = (state: DashboardAppState): void => {
  state.viewsSlice.hasUnsavedChanges = false;
  // change filters
  const allFiltersConfiguration: Array<FilterConfigFrontend> = selectDashboardFilterConfigs(state);

  const activeViewConfiguration: DashboardSmartView = selectActiveViewConfiguration(state);
  const activeViewFilters: Array<Filter> = activeViewConfiguration.filters;
  const activeViewFiltersKeys: Array<FilterKeys> = activeViewFilters.map(
    (filter: Filter): FilterKeys => Object.keys(filter)[0] as FilterKeys
  );

  const filterUpdates: Array<Update<UnifiedFilterConfig>> = allFiltersConfiguration.map(filterConfig => {
    // if the filter is not saved on the view, make it inactive by setting value to undefined
    if (!activeViewFiltersKeys.includes(filterConfig.key)) {
      return {
        id: filterConfig.key,
        changes: {
          ...filterConfig,
          value: undefined,
        },
      };
    }
    // get filter value from view
    const filterFromView: Filter | undefined = activeViewFilters.find(
      (filter: Filter): boolean => (Object.keys(filter)[0] as FilterKeys) === filterConfig.key
    );

    if (!filterFromView) {
      throw new Error(`Filter ${filterConfig.key} not found in active view id ${activeViewConfiguration.id}`);
    }

    const filterValue: AllFilterTypeConditions | undefined = Object.values(filterFromView)[0];

    // set filter value from view
    return {
      id: filterConfig.key,
      changes: {
        ...filterConfig,
        value: filterValue,
      },
    };
  });

  filtersAdapter.updateMany(state.filtersSlice.filters, filterUpdates);

  // Handling the case in which we need to disable or reenable the filters bar if we don't have any filters to show
  // The selector already gives us only the filters with a value
  const updatedFilters: Array<Filter> = selectActiveFilters(state).map((filter: UnifiedFilterConfig) => ({
    [filter.key]: filter.value,
  }));
  const shouldDisplayFiltersBar = FiltersHelper.shouldDisplayFiltersBar(updatedFilters);
  state.uiSlice.ui.hasFiltersToggle = shouldDisplayFiltersBar;
  state.uiSlice.ui.filtersPanelActive = shouldDisplayFiltersBar;

  const smartViewWidgets = activeViewConfiguration.widgets;
  const allWidgets = selectWidgets(state);
  const widgetUpdates: Array<Update<DashboardReduxWidget>> = allWidgets.map(widget => {
    const smartViewWidgetConfig = smartViewWidgets.find(smartViewWidget => widget.id === smartViewWidget.id);

    if (smartViewWidgetConfig) {
      return {
        id: widget.id,
        changes: {
          activeLayout: {
            ...smartViewWidgetConfig.defaultLayout,
          },
          isActive: true,
          isSelected: true,
        },
      };
    }

    return {
      id: widget.id,
      changes: {
        activeLayout: undefined,
        isActive: false,
        isSelected: false,
      },
    };
  });

  widgetsAdapter.updateMany(state.widgetsSlice.widgets, widgetUpdates);
};

export const switchActiveSmartViewFromPanel = (state: DashboardAppState, newActiveViewId: string): void => {
  const currentActiveViewId: string = selectActiveViewId(state);
  smartViewsAdapter.updateMany(state.viewsSlice.views, [
    {
      id: currentActiveViewId,
      changes: { active: false },
    },
    {
      id: newActiveViewId,
      changes: { active: true },
    },
  ]);
  state.viewsSlice.activeSmartViewId = newActiveViewId;
};

export const changeActiveSmartViewFulfilledActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<DashboardSmartView>
): void => {
  const allWidgets = selectWidgets(state);
  const forbiddenWidgetIds = allWidgets.filter(widget => widget.config.forbidden).map(widget => widget.id);
  const acceptedSmartViewWidgets = action.payload.widgets.filter(widget => !forbiddenWidgetIds.includes(widget.id));
  const isViewDefault = action.payload.type === ViewType.DEFAULT;

  smartViewsAdapter.updateOne(state.viewsSlice.views, {
    id: action.payload.id,
    changes: {
      ...action.payload,
      widgets: isViewDefault ? acceptedSmartViewWidgets : action.payload.widgets,
    },
  });

  switchActiveSmartViewFromPanel(state, action.payload.id);
  toggleLiveViewActiveStateActionHandler(state, {
    payload: false,
    type: '',
  });
  setReduxStateFromSmartViewConfiguration(state);
  state.viewsSlice.gridStackFirstInitOfWidgets = true;
  state.viewsSlice.gridStackResetNeeded = true;
};

export const renameSmartView = createAsyncThunk<DashboardSmartView, RenameViewActionInterface>(
  DashboardsReduxActions.RENAME_SMART_VIEW,
  async (payload: RenameViewActionInterface, { getState }): Promise<DashboardSmartView> => {
    const rootState: RootState = getState() as RootState;
    const dashboardAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardAppState).dashboardType;

    const request: UpdateViewRequest = {
      dashboardType: dashboardType,
      viewConfig: { ...payload.smartView, label: payload.newName },
    };
    const response: UpdateViewResponse = await MicroserviceRequest.makeRequest<UpdateViewRequest, UpdateViewResponse>(
      GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME,
      GenericDashboardApiMethods.UPDATE_VIEW,
      request
    );

    return {
      ...response.viewConfig,
      editable: payload.smartView.editable,
      active: payload.smartView.active,
    };
  }
);

export const renameSmartViewFulfilledActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<DashboardSmartView>
): void => {
  smartViewsAdapter.upsertOne(state.viewsSlice.views, action.payload);
};

export const updateSmartViewsSearchText = createAction<string | undefined>(
  DashboardsReduxActions.UPDATE_SMART_VIEWS_SEARCH_TEXT
);

export const updateSmartViewsSearchTextActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<string>
): void => {
  state.viewsSlice.searchText = action.payload ?? '';
};

export const createSmartViewFromActiveConfiguration = (dashboardsAppState: DashboardAppState): DashboardSmartView => {
  const activeViewConfiguration: DashboardSmartView = selectActiveViewConfiguration(dashboardsAppState);
  const dashboardSmartView: DashboardSmartView = cloneDeep(activeViewConfiguration);

  const newFilters: Array<Filter> = selectDashboardFilterValues(dashboardsAppState);
  const activeWidgets = selectActiveWidgets(dashboardsAppState);
  dashboardSmartView.widgets = activeWidgets.map(widget => {
    if (!widget.activeLayout) {
      throw new Error('Active layout is not set for widget.');
    }

    return {
      id: widget.id,
      defaultLayout: widget.activeLayout,
    };
  });
  dashboardSmartView.filters = newFilters;

  dashboardSmartView.filters = newFilters;

  return dashboardSmartView;
};

export const saveSmartViewConfiguration = createAsyncThunk<DashboardSmartView, undefined>(
  DashboardsReduxActions.SAVE_SMART_VIEW_CONFIGURATION,
  async (payload: undefined, { getState, rejectWithValue }) => {
    const rootState: RootState = getState() as RootState;
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardsAppState).dashboardType;

    const dashboardSmartView: DashboardSmartView = createSmartViewFromActiveConfiguration(dashboardsAppState);

    const request: UpdateViewRequest = {
      viewConfig: dashboardSmartView,
      dashboardType: dashboardType,
    };

    try {
      const response: UpdateViewResponse = await MicroserviceRequest.makeRequest<UpdateViewRequest, UpdateViewResponse>(
        GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME,
        GenericDashboardApiMethods.UPDATE_VIEW,
        request
      );

      return {
        ...response.viewConfig,
        editable: dashboardSmartView.editable,
        active: dashboardSmartView.active,
      };
    } catch (error) {
      return rejectWithValue({ label: dashboardSmartView.label });
    }
  }
);
export const saveSmartViewConfigurationFulfilledActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<DashboardSmartView>
): void => {
  // inactive widgets should not have active layout anymore after saving the view
  const inactiveWidgetsUpdater = selectInactiveWidgets(state).map(widget => {
    return {
      id: widget.id,
      changes: {
        ...widget,
        activeLayout: undefined,
      },
    };
  });
  widgetsAdapter.updateMany(state.widgetsSlice.widgets, inactiveWidgetsUpdater);

  smartViewsAdapter.updateOne(state.viewsSlice.views, {
    id: action.payload.id,
    changes: {
      ...action.payload,
    },
  });

  state.viewsSlice.hasUnsavedChanges = false;
};

export const saveSmartViewConfigurationRejectedActionHandler = (state: DashboardAppState, action): void => {
  const viewName = action.payload.label;

  const toastNotificationConfigs: GzToast = {
    message: translateFn('Failed to save {viewName}', { viewName: viewName }),
    hasAction: false,
    severity: ToastSeverity.ERROR,
    keepOpen: false,
    autoCloseDelayMs: 10000,
  };

  GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
};

export const discardSmartViewChanges = createAction(DashboardsReduxActions.DISCARD_SMART_VIEW_CHANGES);
export const discardSmartViewChangesActionHandler = (state: DashboardAppState) => {
  state.viewsSlice.gridStackResetNeeded = true;
  setReduxStateFromSmartViewConfiguration(state);
};

export const discardSmartViewChangesAsync = createAsyncThunk<boolean, boolean>(
  DashboardsReduxActions.DISCARD_SMART_VIEW_CHANGES_ASYNC,
  async (shouldShowDialogAgain: boolean, { getState }): Promise<boolean> => {
    const rootState: RootState = getState() as RootState;
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardsAppState).dashboardType;

    if (selectShouldDisplayDiscardPopUp(dashboardsAppState) === shouldShowDialogAgain) {
      // If the user didn't change anything, we shouldn't make a useless backend call
      return shouldShowDialogAgain;
    }

    const request: UpdateDashboardConfigForUserRequest = {
      dashboardType,
      changedProps: {
        shouldDisplayDiscard: shouldShowDialogAgain,
      },
    };

    await MicroserviceRequest.makeRequest<UpdateDashboardConfigForUserRequest, UpdateDashboardConfigForUserResponse>(
      GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME,
      GenericDashboardApiMethods.UPDATE_DASHBOARD_CONFIG_FOR_USER,
      request
    );

    return shouldShowDialogAgain;
  }
);
export const discardSmartViewChangesAsyncFulfilledActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<boolean>
): void => {
  state.uiSlice.configuration.popUp.shouldDisplayDiscard = action.payload;
  setReduxStateFromSmartViewConfiguration(state);
  state.viewsSlice.gridStackResetNeeded = true;
};

export const setShouldDisplayUnsavedChangesPopUp = createAsyncThunk<boolean, boolean>(
  DashboardsReduxActions.DISCARD_SMART_VIEW_CHANGES,
  async (shouldShowDialogAgain: boolean, { getState }): Promise<boolean> => {
    const rootState: RootState = getState() as RootState;
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardsAppState).dashboardType;

    if (selectShouldDisplayUnsavedChangesPopUp(dashboardsAppState) === shouldShowDialogAgain) {
      // If the user didn't change anything, we shouldn't make a useless backend call
      return shouldShowDialogAgain;
    }

    const request: UpdateDashboardConfigForUserRequest = {
      dashboardType,
      changedProps: {
        shouldDisplayUnsavedChanges: shouldShowDialogAgain,
      },
    };

    await MicroserviceRequest.makeRequest<UpdateDashboardConfigForUserRequest, UpdateDashboardConfigForUserResponse>(
      GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME,
      GenericDashboardApiMethods.UPDATE_DASHBOARD_CONFIG_FOR_USER,
      request
    );

    return shouldShowDialogAgain;
  }
);

export const setShouldDisplayUnsavedChangesPopUpFulfilledHandler = (
  state: DashboardAppState,
  action: PayloadAction<boolean>
): void => {
  state.uiSlice.configuration.popUp.shouldDisplayUnsavedChanges = action.payload;
};

export const createNewSmartView = createAsyncThunk<DashboardSmartView, string>(
  DashboardsReduxActions.CREATE_NEW_SMART_VIEW_CONFIGURATION,
  async (payload: string, { getState, rejectWithValue }) => {
    const rootState: RootState = getState() as RootState;
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardsAppState).dashboardType;

    const dashboardSmartView: DashboardSmartView = createSmartViewFromActiveConfiguration(dashboardsAppState);

    dashboardSmartView.label = payload;

    const dashboardSmartViewBase: DashboardSmartViewBase = { ...dashboardSmartView };
    dashboardSmartViewBase.categoryGroup = CategoryGroup.SAVED;
    dashboardSmartViewBase.type = ViewType.CUSTOM;

    const request: CreateViewRequest = {
      dashboardType: dashboardType,
      viewConfig: dashboardSmartViewBase,
    };

    try {
      const response: CreateViewResponse = await MicroserviceRequest.makeRequest<CreateViewRequest, CreateViewResponse>(
        GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME,
        GenericDashboardApiMethods.CREATE_VIEW,
        request
      );

      return {
        ...response.viewConfig,
        editable: dashboardSmartView.editable,
        active: dashboardSmartView.active,
      };
    } catch (e) {
      return rejectWithValue({ label: payload });
    }
  }
);

export const createNewSmartViewFulfilledActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<DashboardSmartView>
): void => {
  smartViewsAdapter.upsertOne(state.viewsSlice.views, action.payload);
  switchActiveSmartViewFromPanel(state, action.payload.id);
  setReduxStateFromSmartViewConfiguration(state);

  if (!state.uiSlice.configuration.smartViewsPanel.visible) {
    return;
  }

  const toastNotificationConfigs: GzToast = {
    message: translateFn('Successfully created {viewName}', { viewName: action.payload.label }),
    hasAction: false,
    severity: ToastSeverity.SUCCESS,
    keepOpen: false,
    autoCloseDelayMs: 10000,
  };

  GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
};

export const createNewSmartViewRejectedActionHandler = (state: DashboardAppState, action): void => {
  const viewName = action.payload.label;

  const toastNotificationConfigs: GzToast = {
    message: translateFn('Failed to create {viewName}', { viewName: viewName }),
    hasAction: false,
    severity: ToastSeverity.ERROR,
    keepOpen: false,
    autoCloseDelayMs: 10000,
  };

  GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
};

export const deleteSmartView = createAsyncThunk<string, string, { rejectValue: string }>(
  DashboardsReduxActions.DELETE_SMART_VIEW,
  async (payload: string, { rejectWithValue, getState }) => {
    const rootState: RootState = getState() as RootState;
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardsAppState).dashboardType;

    const request: DeleteViewRequest = {
      viewId: payload,
      dashboardType: dashboardType,
    };

    try {
      const response: DeleteViewResponse = await MicroserviceRequest.makeRequest<DeleteViewRequest, DeleteViewResponse>(
        GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME,
        GenericDashboardApiMethods.DELETE_VIEW,
        request
      );

      return payload;
    } catch (error) {
      return rejectWithValue(payload);
    }
  }
);

export const deleteSmartViewFulfilledActionHandler = (
  state: DashboardAppState,
  action: PayloadAction<string>
): void => {
  const viewLabel: string = selectSmartViewById(state, action.payload).label;

  const toastNotificationConfigs: GzToast = {
    message: `Successfully deleted ${viewLabel}`,
    hasAction: false,
    severity: ToastSeverity.SUCCESS,
    keepOpen: false,
    autoCloseDelayMs: 10000,
  };

  GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);

  smartViewsAdapter.removeOne(state.viewsSlice.views, action.payload);
};

export const deleteSmartViewRejectedActionHandler = (state: DashboardAppState, action): void => {
  const viewLabel: string = selectSmartViewById(state, action.payload).label;
  const toastNotificationConfigs: GzToast = {
    message: `Failed to delete view ${viewLabel}`,
    hasAction: false,
    severity: ToastSeverity.ERROR,
    keepOpen: false,
    autoCloseDelayMs: 10000,
  };

  GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
};

export const toggleSmartViewsPanelIsCollapsed = createAsyncThunk<boolean, boolean, { rejectValue: boolean }>(
  DashboardsReduxActions.TOGGLE_SMART_VIEWS_PANEL_COLLAPSED_STATE,
  async (payload: boolean, { rejectWithValue, getState }) => {
    const rootState: RootState = getState() as RootState;
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const dashboardType: DashboardType = dashboardConfigurationSelector(dashboardsAppState).dashboardType;

    const request: SetSmartViewsPanelStatusRequest = {
      dashboardType: dashboardType,
      isSmartViewsPanelOpen: !payload,
    };

    try {
      const response: SetSmartViewsPanelStatusResponse = await MicroserviceRequest.makeRequest<
        SetSmartViewsPanelStatusRequest,
        SetSmartViewsPanelStatusResponse
      >(GENERIC_DASHBOARD_MICROSERVICE_CLIENT_NAME, GenericDashboardApiMethods.SET_SMART_VIEWS_PANEL_STATUS, request);

      return !payload;
    } catch (error) {
      return rejectWithValue(!payload);
    }
  }
);

export const toggleSmartViewsPanelCollapseFulfilledHandler = (state: DashboardAppState, action) => {
  if (state.uiSlice.configuration.smartViewsPanel) {
    state.uiSlice.configuration.smartViewsPanel.isOpen = action.payload;
  }
};
export const toggleSmartViewsPanelCollapseRejectedHandler = (state: DashboardAppState, action) => {
  if (state.uiSlice.configuration.smartViewsPanel) {
    state.uiSlice.configuration.smartViewsPanel.isOpen = action.payload;
  }
};
